import React, { useState, useContext } from 'react';
import { Button, Modal, TextField } from '@mui/material';
import { Box } from '@mui/system';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { NotificationContext } from '../../contexts/Notification';

/** The server stores working hours as HH:mm:ss; the time input gives HH:mm. */
const toServerTime = (value) => (value ? `${value}:00` : '');
const toInputTime = (value) => (value ? String(value).slice(0, 5) : '');

/**
 * Raised from the Schedule Adherence alert email when the employee believes the
 * shift they were judged against is wrong. This only creates a request — the
 * member's hours are not touched until their reporting manager approves it on
 * the Profile Update Requests screen.
 */
function WorkingHoursUpdateModal({ closeModal, member, setData }) {
  const { displayNotification } = useContext(NotificationContext);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    workingDayStartTime: toInputTime(member?.workingDayStartTime),
    workingDayEndTime: toInputTime(member?.workingDayEndTime),
    workingScheduleTimezone: member?.workingScheduleTimezone || moment.tz.guess(),
    reason: '',
  });

  const setField = (field) => (event) => setForm(
    (prev) => ({ ...prev, [field]: event.target.value }),
  );

  // The server rejects a request that proposes nothing, so do not let one be sent.
  const hasProposal = Boolean(
    form.workingDayStartTime || form.workingDayEndTime || form.workingScheduleTimezone,
  );

  const handleSubmit = async () => {
    setSaving(true);

    try {
      const response = await sendRequest({
        method: 'post',
        url: API.USER.updateWorkingHoursRequest,
        data: {
          workingDayStartTime: toServerTime(form.workingDayStartTime),
          workingDayEndTime: toServerTime(form.workingDayEndTime),
          workingScheduleTimezone: form.workingScheduleTimezone,
          reason: form.reason,
        },
      });

      if (response.success) {
        if (setData) setData(response.data.profileUpdateRequest);
        displayNotification({ message: response.data.message, severity: 'success' });
        closeModal();
      } else {
        displayNotification({ message: response.data.error, severity: 'error' });
      }
    } catch (error) {
      displayNotification({
        message: error?.response?.data?.error || 'Could not submit the request.',
        severity: 'error',
      });
    }

    setSaving(false);
  };

  return (
    <Modal
      open
      onClose={closeModal}
      className="flex-row align-c jstfy-c"
      data-testid="working-hours-update-modal"
    >
      <Box style={{
        width: 500,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: 32,
        background: '#fff',
        borderRadius: 8,
      }}
      >
        <h5 className="m-b20 text-600" data-testid="modal-title">Request Working Hours Change</h5>
        <p className="m-b20 text-sm text-ink-700">
          Your schedule adherence is measured against these hours. Proposing a change sends
          it to your reporting manager for approval &mdash; nothing changes until they approve it.
        </p>

        <div className="flex gap-4 m-b20">
          <TextField
            label="Shift start"
            type="time"
            variant="standard"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'working-hours-start' }}
            value={form.workingDayStartTime}
            onChange={setField('workingDayStartTime')}
          />
          <TextField
            label="Shift end"
            type="time"
            variant="standard"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'working-hours-end' }}
            value={form.workingDayEndTime}
            onChange={setField('workingDayEndTime')}
          />
        </div>

        <TextField
          select
          label="Timezone"
          variant="standard"
          fullWidth
          className="m-b20"
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          value={form.workingScheduleTimezone}
          onChange={setField('workingScheduleTimezone')}
        >
          {moment.tz.names().map((tz) => <option key={tz} value={tz}>{tz}</option>)}
        </TextField>

        <TextField
          label="Reason (optional)"
          variant="standard"
          fullWidth
          multiline
          rows={2}
          className="m-b20"
          placeholder="e.g. my shift moved to afternoons in June"
          value={form.reason}
          onChange={setField('reason')}
        />

        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          sx={{ marginRight: 2, marginTop: 2 }}
          disabled={saving || !hasProposal}
          data-testid="working-hours-submit"
        >
          {saving ? 'Submitting...' : 'Submit request'}
        </Button>
        <Button
          onClick={closeModal}
          color="secondary"
          variant="outlined"
          sx={{ marginTop: 2 }}
        >
          Cancel
        </Button>
      </Box>
    </Modal>
  );
}

WorkingHoursUpdateModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  member: PropTypes.shape({
    workingDayStartTime: PropTypes.string,
    workingDayEndTime: PropTypes.string,
    workingScheduleTimezone: PropTypes.string,
  }),
  setData: PropTypes.func,
};

WorkingHoursUpdateModal.defaultProps = {
  member: null,
  setData: null,
};

export default WorkingHoursUpdateModal;
